import { useMutation, useQueryClient } from '@tanstack/react-query';

import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { cohortQueryKeys } from './queryKeys';

export interface AutosaveDraftInput {
  projectId: string;
  cohortId: string;
  jsonValue: CohortBuilderDefinitionLegacy;
  signal?: AbortSignal;
}

/**
 * Server-reported staged-changes state after persisting a draft. Mirrors the
 * `staged_changes_updated` socket payload the legacy autosave path returned,
 * which the FE store consumes to clear `isSaving` and reconcile
 * `hasStagedChanges` / `latestStatus`.
 */
export interface AutosaveDraftResponse {
  hasStagedChanges: boolean;
  versionId?: string | null;
  newStatus?: string | null;
}

/**
 * Canonical TanStack mutation for the cohort-builder autosave (gen-3 path).
 * Replaces the legacy `useAutosaveCohortMutation` which round-tripped through
 * the Research Assistant socket — see OR-6101. The new endpoint
 * (`PUT /v1/.../cohorts/{project_id}/cohort-profile/{cohort_id}/draft`) writes
 * the draft to the same `cohort_draft_service.write_draft` path, so the
 * net-zero detection and `has_staged_changes` semantics are identical to the
 * legacy socket flow.
 *
 * Callers own:
 * - `setIsSaving` toggling around the mutate call (or via `onMutate` /
 *   `onSettled` of their wrapper hook),
 * - reconciling `hasStagedChanges` / `latestStatus` from the response payload.
 *
 * The mutation invalidates the cohort profile cache on success so any
 * version/status changes (DRAFT → UNSAVED, version bump) round-trip from the
 * server. Definition hydration is owned by the caller because the autosave
 * response does not carry the full DSL — the FE's optimistic store state is
 * authoritative until the next definition query refetches.
 */
export const useAutosaveDraftMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<AutosaveDraftResponse, Error, AutosaveDraftInput>({
    mutationFn: async ({ projectId, cohortId, jsonValue, signal }) => {
      const url = `/${COHORTS_PATH}/${projectId}/cohort-profile/${cohortId}/draft`;
      const response = await authFetch<ObjectResponse<AutosaveDraftResponse>>(url, {
        method: 'PUT',
        body: JSON.stringify({ jsonValue }),
        signal,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cohortQueryKeys.all });
    },
  });
};
